import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import PageTopBar from "../../../components/common/PageTopBar";
import { fieldHintTooltipProps, toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import { useStudentImport } from "./hooks/useStudentImport";
import { duesPlanForGrade } from "./service/studentImportService";

const STEPS = ["Download Template", "Upload & Validate", "Import"];

const formatAmount = (amount: number) => amount.toLocaleString("en-IN");
const formatSize = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

const stickyHeadSx = { "& th": { fontWeight: 600, backgroundColor: "grey.50", whiteSpace: "nowrap" } };

/** Section card used by each step: title, one-line description, then the step's content. */
const StepCard: React.FC<{ title: string; description: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
      {description}
    </Typography>
    {children}
  </Paper>
);

/** Result number tile on the import result screen. */
const StatTile: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, borderTop: 3, borderTopColor: color }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700}>
      {value}
    </Typography>
  </Paper>
);

/**
 * Students → Import: download the template, upload it and fix any problems, then import.
 * All checks run in the browser before anything is sent; the backend repeats them per student.
 */
const StudentImportPage: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [templateGrade, setTemplateGrade] = useState("");
  const [templateSection, setTemplateSection] = useState("");
  const {
    rules,
    rulesError,
    duesHeaders,
    downloading,
    downloadTemplate,
    file,
    parsing,
    parseResult,
    isValid,
    readFile,
    resetFile,
    importing,
    outcome,
    importError,
    submit,
    downloadErrorReport,
    downloadFailedRows,
  } = useStudentImport();

  const openFilePicker = () => fileRef.current?.click();

  const handleFiles = (files: FileList | null) => {
    const selected = files?.[0];
    if (selected) readFile(selected);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startOver = () => {
    resetFile();
    setActiveStep(1);
  };

  // Template is made for the chosen grade + section; its dues columns depend on the grade
  const gradeOptions = rules?.orderedGrades ?? [];
  const selectedGrade = gradeOptions.find((g) => g.name === templateGrade);
  const plan = rules && templateGrade ? duesPlanForGrade(rules, templateGrade) : null;
  // Only years the admin can fix are called out; years before the lowest grade are expected
  const fixableExclusions = plan ? plan.excluded.filter((e) => e.fixable) : [];
  const issueCount = parseResult ? parseResult.fileErrors.length + parseResult.rowIssues.length : 0;
  const done = outcome !== null;

  const canGoNext = activeStep === 0 || (activeStep === 1 && isValid && !parsing);

  return (
    <Box>
      <PageTopBar left="Import Students" />

      <Stepper activeStep={done ? STEPS.length : activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ============ STEP 1: DOWNLOAD TEMPLATE ============ */}
      {activeStep === 0 && (
        <StepCard
          title="Download the template"
          description="Download the template and follow the Instructions sheet inside it. It explains every column and rule the upload checks."
        >
          {rulesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rulesError}
            </Alert>
          )}
          {!rules && !rulesError && <LinearProgress sx={{ mb: 2 }} />}

          {rules && (
            <>
              {/* Grade + Section the template is made for */}
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 2, ...toolbarFieldsSx }}>
                <TextField
                  select
                  size="small"
                  label="Grade"
                  value={templateGrade}
                  onChange={(e) => {
                    setTemplateGrade(e.target.value);
                    setTemplateSection("");
                  }}
                  sx={{ minWidth: 140 }}
                >
                  {gradeOptions.map((g) => (
                    <MenuItem key={g.id} value={g.name} sx={{ fontSize: 14 }}>
                      {g.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Section"
                  value={templateSection}
                  onChange={(e) => setTemplateSection(e.target.value)}
                  sx={{ minWidth: 120 }}
                  disabled={!selectedGrade}
                >
                  {(selectedGrade?.sections ?? []).map((s) => (
                    <MenuItem key={s.id} value={s.name} sx={{ fontSize: 14 }}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  size="small"
                  sx={toolbarButtonSx}
                  startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  onClick={() => downloadTemplate(templateGrade, templateSection)}
                  disabled={downloading || !templateGrade || !templateSection || !!plan?.blocker}
                >
                  {downloading ? "Preparing..." : "Download Template"}
                </Button>
              </Box>
              {selectedGrade && selectedGrade.sections.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Grade {selectedGrade.name} has no sections. Add one under Academics → Setup first.
                </Alert>
              )}
              {/* The current-year dues are required, so without that fee (or an ACTIVE year) nothing can be imported */}
              {plan?.blocker && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5 }}
                  action={
                    rules.currentYear ? (
                      <Button color="inherit" size="small" onClick={() => navigate("/app/fees/structures")}>
                        Fee Structures
                      </Button>
                    ) : undefined
                  }
                >
                  {plan.blocker}
                </Alert>
              )}

              {/* Dues summary for the chosen grade: one line however many years; max per column on hover */}
              {plan && (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Dues columns for Grade {templateGrade}:
                  </Typography>
                  {plan.columns.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      none
                    </Typography>
                  ) : (
                    <Tooltip
                      title={
                        <Box>
                          {plan.columns.map((c) => (
                            <div key={c.year}>
                              {c.year} ({c.gradeThen}): max {formatAmount(c.max)}
                            </div>
                          ))}
                        </Box>
                      }
                      {...fieldHintTooltipProps}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ cursor: "help" }}
                        label={
                          plan.columns.length === 1
                            ? `1 year, ${plan.columns[0].year}`
                            : `${plan.columns.length} years, ${plan.columns[0].year} → ${plan.columns[plan.columns.length - 1].year}`
                        }
                      />
                    </Tooltip>
                  )}
                </Box>
              )}

              {fixableExclusions.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate("/app/fees/structures")}>
                      Fee Structures
                    </Button>
                  }
                >
                  No dues column for{" "}
                  {fixableExclusions.map((e, index) => (
                    <React.Fragment key={e.year}>
                      {index > 0 && "; "}
                      <b>{e.year}</b> ({e.reason})
                    </React.Fragment>
                  ))}
                  .
                </Alert>
              )}

              <Box component="ul" sx={{ m: 0, mt: 2, pl: 2.5, "& li": { typography: "body2", color: "text.secondary", mb: 0.5 } }}>
                <li>The template is made for the chosen grade and section. Every student in the file goes there.</li>
                <li>Each dues cell is what the student still owes for that year; 0 = fully paid. The current year ({rules.currentYear ?? "—"}) is required for every student; leave a past year blank if the student was not in school then. The maximum is shown when you click the cell.</li>
                <li>Each imported student becomes ACTIVE, with an enrollment for every year and the dues split into 4 terms.</li>
                <li>Download a fresh template whenever academic years or fee structures change.</li>
              </Box>
            </>
          )}
        </StepCard>
      )}

      {/* ============ STEP 2: UPLOAD & VALIDATE ============ */}
      {activeStep === 1 && (
        <StepCard title="Upload and check the file" description="Every row is checked before anything is imported. Fix the problems in Excel and upload the file again.">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => handleFiles(e.target.files)} />

          {!file ? (
            // Drop zone
            <Box
              onClick={openFilePicker}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer.files);
              }}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: dragOver ? "primary.main" : "grey.300",
                bgcolor: dragOver ? "rgba(25, 118, 210, 0.04)" : "grey.50",
                borderRadius: 2,
                py: 5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 150ms ease",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography fontWeight={600}>Drop the filled template here, or click to browse</Typography>
              <Typography variant="body2" color="text.secondary">
                Excel file (.xlsx)
              </Typography>
            </Box>
          ) : (
            <>
              {/* File summary */}
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ p: 1.5, mb: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <InsertDriveFileOutlinedIcon color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatSize(file.size)}
                    {parseResult?.importInfo?.grade && ` · Grade ${parseResult.importInfo.grade}`}
                    {parseResult?.importInfo?.section && ` · Section ${parseResult.importInfo.section}`}
                    {parseResult && parseResult.fileErrors.length === 0 && ` · ${parseResult.rows.length + parseResult.rowIssues.length} students`}
                  </Typography>
                </Box>
                <Box flexGrow={1} />
                {parsing ? (
                  <Chip size="small" label="Checking..." />
                ) : isValid ? (
                  <Chip size="small" color="success" icon={<CheckCircleOutlineIcon />} label="All rows valid" />
                ) : (
                  parseResult && <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label={`${issueCount} ${issueCount === 1 ? "problem" : "problems"}`} />
                )}
                {!parsing && issueCount > 0 && (
                  <Button size="small" onClick={downloadErrorReport}>
                    Download problems
                  </Button>
                )}
                <Button size="small" variant="outlined" onClick={openFilePicker} disabled={parsing}>
                  Choose another file
                </Button>
              </Box>
              {parsing && <LinearProgress sx={{ mb: 2 }} />}

              {parseResult && parseResult.fileErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {parseResult.fileErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </Box>
                </Alert>
              )}

              {/* Row problems */}
              {parseResult && parseResult.rowIssues.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{parseResult.rowIssues.length}</b> of {parseResult.rows.length + parseResult.rowIssues.length} students have problems. Nothing is imported until all are fixed.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader sx={stickyHeadSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Admission No</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Problem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parseResult.rowIssues.map((issue) => (
                          <TableRow key={issue.rowNumber} hover sx={{ verticalAlign: "top" }}>
                            <TableCell>{issue.rowNumber}</TableCell>
                            <TableCell>{issue.admissionNo || "—"}</TableCell>
                            <TableCell>{issue.name || "—"}</TableCell>
                            <TableCell>
                              <Box component="ul" sx={{ m: 0, pl: 2, "& li": { typography: "body2" } }}>
                                {issue.problems.map((problem) => (
                                  <li key={problem}>{problem}</li>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Preview of valid rows */}
              {isValid && parseResult && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader sx={stickyHeadSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Admission No</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Gender</TableCell>
                        <TableCell>DOB</TableCell>
                        <TableCell>Guardian</TableCell>
                        {duesHeaders.map((header) => (
                          <TableCell key={header} align="right">
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parseResult.rows.map((row) => (
                        <TableRow key={row.rowNumber} hover>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell>{row.admissionNo}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.data["Gender *"]}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{row.data["DOB(DD-MMM-YYYY) *"]}</TableCell>
                          <TableCell>{row.data["Guardian - Name *"]}</TableCell>
                          {rules?.duesYears.map((year) => (
                            <TableCell key={year} align="right" sx={{ color: row.dues[year] === undefined ? "text.disabled" : "text.primary" }}>
                              {row.dues[year] === undefined ? "—" : formatAmount(row.dues[year])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </StepCard>
      )}

      {/* ============ STEP 3: IMPORT ============ */}
      {activeStep === 2 && parseResult?.importInfo && (
        <StepCard
          title={done ? "Import finished" : "Import the students"}
          description={
            done
              ? "Students that failed were not created. Download them, fix the problems and import them again."
              : `${parseResult.rows.length} students will be created in Grade ${parseResult.importInfo.grade} - ${parseResult.importInfo.section}, with their dues for each year entered.`
          }
        >
          {importError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={submit} disabled={importing}>
                  Try again
                </Button>
              }
            >
              {importError}
            </Alert>
          )}

          {!done && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="contained" color="success" size="large" onClick={submit} disabled={importing} startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadOutlinedIcon />}>
                {importing ? "Importing..." : `Import ${parseResult.rows.length} students`}
              </Button>
              {importing && (
                <Typography variant="body2" color="text.secondary">
                  This can take a minute for a large file. Don't close this page.
                </Typography>
              )}
            </Box>
          )}

          {outcome && (
            <>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2.5 }}>
                <StatTile label="Sent" value={outcome.sent} color="grey.400" />
                <StatTile label="Imported" value={outcome.imported} color="success.main" />
                <StatTile label="Failed" value={outcome.failed.length} color={outcome.failed.length > 0 ? "error.main" : "grey.300"} />
              </Stack>

              {outcome.failed.length === 0 ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  All {outcome.imported} students were imported.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, mb: 2 }}>
                  <Table size="small" stickyHeader sx={stickyHeadSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Admission No</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {outcome.failed.map((row, index) => (
                        <TableRow key={`${row.admissionNo}-${index}`} hover>
                          <TableCell>{row.admissionNo || "—"}</TableCell>
                          <TableCell>{row.name || "—"}</TableCell>
                          <TableCell>{row.message || row.status || "FAILED"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Stack direction="row" spacing={1}>
                {outcome.failed.length > 0 && (
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadFailedRows}>
                    Download failed students
                  </Button>
                )}
                <Button variant="contained" onClick={startOver}>
                  Import another file
                </Button>
                <Button onClick={() => navigate("/app/students")}>Go to Students</Button>
              </Stack>
            </>
          )}
        </StepCard>
      )}

      {/* Navigation (hidden while importing and once the import is done) */}
      {!importing && !done && (
        <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button startIcon={<NavigateBeforeIcon />} onClick={() => setActiveStep((step) => step - 1)} disabled={activeStep === 0}>
            Back
          </Button>
          {activeStep < STEPS.length - 1 && (
            <Tooltip title={activeStep === 1 && !isValid ? "Upload a file with no problems to continue" : ""} {...fieldHintTooltipProps}>
              <span>
                <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={() => setActiveStep((step) => step + 1)} disabled={!canGoNext}>
                  Next
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentImportPage;
